import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Loader2,
  Lock,
  RefreshCcw,
  Trash2,
  Users as UsersIcon,
  ShieldCheck,
  Mail,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const ROLES = ["owner", "editor", "viewer", "user"];

const ROLE_TONE = {
  owner: "bg-primary text-primary-foreground",
  editor: "bg-accent text-accent-foreground",
  viewer: "bg-secondary text-secondary-foreground",
  user: "bg-muted text-muted-foreground",
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [me, setMe] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, meRes] = await Promise.all([
        api.get("/users"),
        api.get("/auth/me").catch(() => ({ data: null })),
      ]);
      setUsers(list);
      setMe(meRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const changeRole = async (uid, role) => {
    try {
      await api.put(`/users/${uid}/role`, { role });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const del = async (uid, email) => {
    if (!confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${uid}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const isOwner = me?.role === "owner";
  const filtered = users.filter(
    (u) =>
      !q ||
      (u.email + " " + u.name + " " + (u.city || "") + " " + (u.role || ""))
        .toLowerCase()
        .includes(q.toLowerCase())
  );

  return (
    <div className="p-6 lg:p-10 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-8 animate-in-up flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-primary mb-2">
            Team
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">
            Users &amp; Roles
          </h1>
          <p className="text-sm text-muted-foreground">
            {users.length} account{users.length === 1 ? "" : "s"}. Only owners can
            promote or delete users.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          data-testid="users-refresh-btn"
          className="h-10"
        >
          <RefreshCcw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="mb-5 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search email, name, city or role"
          className="pl-9 h-11"
          data-testid="users-search-input"
        />
      </div>

      {loading ? (
        <div className="text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-2xl p-16 text-center">
          <UsersIcon className="w-8 h-8 mx-auto text-muted-foreground mb-3" />
          <div className="text-sm font-medium">
            {users.length === 0 ? "No users yet" : "No matches"}
          </div>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground bg-secondary/40">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">User</th>
                <th className="text-left px-5 py-3 font-semibold">City</th>
                <th className="text-left px-5 py-3 font-semibold">Method</th>
                <th className="text-left px-5 py-3 font-semibold">Role</th>
                <th className="text-right px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.user_id} className="border-t border-border">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center font-bold">
                        {u.picture ? (
                          <img
                            src={u.picture}
                            alt=""
                            className="w-9 h-9 rounded-full"
                          />
                        ) : (
                          u.name?.slice(0, 1).toUpperCase() || "?"
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{u.name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {u.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-muted-foreground">
                    {u.city || "—"}
                  </td>
                  <td className="px-5 py-4">
                    {u.google_linked ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
                        <ShieldCheck className="w-3.5 h-3.5" /> Google
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Email</span>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    {isOwner && u.role !== "owner" ? (
                      <select
                        value={u.role}
                        onChange={(e) => changeRole(u.user_id, e.target.value)}
                        data-testid={`role-select-${u.user_id}`}
                        className="h-9 rounded-lg border border-input bg-background px-2 text-xs font-semibold"
                      >
                        {ROLES.filter((r) => r !== "owner").map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-bold ${
                          ROLE_TONE[u.role] || "bg-secondary"
                        }`}
                        title={u.role === "owner" ? "Owner role is protected" : undefined}
                      >
                        {u.role === "owner" && <Lock className="w-3 h-3" />}
                        {u.role}
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-4 text-right">
                    {isOwner && u.user_id !== me?.user_id && u.role !== "owner" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => del(u.user_id, u.email)}
                        data-testid={`user-delete-${u.user_id}`}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
